/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Estimate Calculations Helper Functions
 * Shared utilities for overhead, contingency, and margin calculations
 */

import type { BudgetRates } from "./estimates-types";

// ============================================================================
// OVERHEAD HELPERS
// ============================================================================

/**
 * Resolve overhead basis from budget rates configuration
 * Determines whether overhead is calculated on direct costs or revenue
 */
export function resolveOverheadBasis(
  budgetRates?: BudgetRates | null,
): "direct" | "revenue" {
  if (budgetRates?.overheadRecoveryMethod === "percentage_revenue") {
    return "revenue";
  }
  if (budgetRates?.overheadRecoveryMethod === "percentage_direct_costs") {
    return "direct";
  }
  return budgetRates?.overheadBasis ?? "direct";
}

export function resolveConfiguredMarginRate(
  budgetRates?: BudgetRates | null,
): number | null {
  if (budgetRates?.profitMargin != null) return budgetRates.profitMargin;
  if (budgetRates?.marginRate != null) return budgetRates.marginRate;
  return null;
}

export function resolveConfiguredMarginPct(
  budgetRates?: BudgetRates | null,
): number {
  const rate = resolveConfiguredMarginRate(budgetRates);
  return (rate ?? 0) * 100;
}

/**
 * Apply overhead cap and floor constraints
 * Ensures overhead stays within configured bounds
 */
export function applyOverheadConstraints(
  overhead: number,
  budgetRates?: BudgetRates | null,
): number {
  let adjusted = overhead;
  if (budgetRates?.overheadCap !== undefined) {
    adjusted = Math.min(adjusted, budgetRates.overheadCap);
  }
  if (budgetRates?.overheadFloor !== undefined) {
    adjusted = Math.max(adjusted, budgetRates.overheadFloor);
  }
  return adjusted;
}

// ============================================================================
// CONTINGENCY HELPERS
// ============================================================================

/**
 * Check if contingency should be applied based on budget configuration
 */
export function shouldApplyContingency(
  budgetRates?: BudgetRates | null,
): boolean {
  return budgetRates?.includeContingency !== false;
}

/**
 * Resolve the base amount for contingency calculation
 * Can be either direct cost only or subtotal (direct + overhead)
 */
export function resolveContingencyBase(
  directCost: number,
  overhead: number,
  budgetRates?: BudgetRates | null,
): number {
  const basis = budgetRates?.contingencyBasis ?? "direct";
  return basis === "subtotal" ? directCost + overhead : directCost;
}

export function resolveContingencyBaseFromBasis(
  directCost: number,
  overhead: number,
  contingencyBasis: "direct" | "subtotal" = "direct",
): number {
  return contingencyBasis === "subtotal" ? directCost + overhead : directCost;
}

// ============================================================================
// REVENUE FORMULA HELPERS
// ============================================================================

/**
 * Check if revenue-based formula should be used for pricing calculations
 * Revenue formula is only valid under specific conditions
 */
export function shouldUseRevenueFormula(
  budgetRates?: BudgetRates | null,
  isActivityBased: boolean = false,
): boolean {
  const overheadBasis = resolveOverheadBasis(budgetRates);
  const overheadRecoveryMethod = budgetRates?.overheadRecoveryMethod;
  const usesCategoryDrivenOverhead =
    isActivityBased ||
    overheadRecoveryMethod === "activity_based" ||
    overheadRecoveryMethod === "labor_hour_absorption";

  return (
    !usesCategoryDrivenOverhead &&
    overheadBasis === "revenue" &&
    overheadRecoveryMethod !== "fixed_amount" &&
    budgetRates?.overheadCap === undefined &&
    budgetRates?.overheadFloor === undefined
  );
}

// ============================================================================
// RATE VALIDATION HELPERS
// ============================================================================

/**
 * Maximum allowed rate value to prevent division by zero.
 * Rates at or above 1.0 would cause division by zero in margin formulas.
 */
export const MAX_SAFE_RATE = 0.99;

/**
 * Validates that a rate is safe for division operations.
 * Returns the clamped rate value if it exceeds safe bounds.
 *
 * @param rate - The rate to validate (as decimal, e.g., 0.15 for 15%)
 * @returns Clamped rate value between 0 and MAX_SAFE_RATE
 */
export function clampRate(rate: number | undefined): number {
  if (rate === undefined || isNaN(rate)) return 0;
  return Math.min(Math.max(rate, 0), MAX_SAFE_RATE);
}

/**
 * Safely calculates a division result, returning a fallback if denominator is zero or near-zero.
 * Used for profit margin and overhead calculations that could divide by zero.
 *
 * @param numerator - The dividend
 * @param denominator - The divisor
 * @param fallback - Value to return if division is unsafe (default: numerator)
 * @returns Safe division result or fallback
 */
export function safeDivide(
  numerator: number,
  denominator: number,
  fallback?: number,
): number {
  // Consider denominators very close to zero as unsafe (epsilon of 0.0001)
  if (Math.abs(denominator) < 0.0001) {
    return fallback ?? numerator;
  }
  return numerator / denominator;
}
